import { jsx as _jsx } from "react/jsx-runtime";
import { useBannerContext } from '#pplx/frontend/aether/components/Banner/useBannerContext.js';
import { Button } from '#pplx/frontend/aether/components/Button/index.js';
import { Link } from '#pplx/frontend/aether/components/Link/index.js';
import { useMeasureableHandle } from '#pplx/frontend/aether/internal/useMeasureableHandle.js';
import { useRadixProps } from '#pplx/frontend/aether/internal/useRadixProps.js';
import classNames from 'classnames';
import React from 'react';
// CSS overrides to tint buttons/links based on banner variant
// Uses Tailwind arbitrary selectors to target child button/anchor elements
// Note: Classes must be explicit strings (not template literals) for Tailwind static analysis
// TODO: Refactor once we introduce accent colors to Button/Link components
const VARIANT_TINT_CLASSES = {
    info: {
        primary: `[&_button]:bg-super-strong [&_button:hover]:opacity-80 [&_a]:bg-super-strong [&_a:hover]:opacity-80`,
        secondary: `[&_button]:bg-transparent [&_button]:border-super-strong [&_button]:text-super-primary [&_button:hover]:bg-super-soft [&_a]:bg-transparent [&_a]:border-super-strong [&_a]:text-super-primary [&_a:hover]:bg-super-soft`,
        text: `[&_button]:text-super-primary [&_button:hover]:bg-super-soft [&_a]:text-super-primary [&_a:hover]:bg-super-soft`,
        tonal: '',
    },
    success: {
        primary: `[&_button]:bg-positive-strong [&_button:hover]:opacity-80 [&_a]:bg-positive-strong [&_a:hover]:opacity-80`,
        secondary: `[&_button]:bg-transparent [&_button]:border-positive-strong [&_button]:text-positive-primary [&_button:hover]:bg-positive-soft [&_a]:bg-transparent [&_a]:border-positive-strong [&_a]:text-positive-primary [&_a:hover]:bg-positive-soft`,
        text: `[&_button]:text-positive-primary [&_button:hover]:bg-positive-soft [&_a]:text-positive-primary [&_a:hover]:bg-positive-soft`,
        tonal: '',
    },
    warning: {
        primary: `[&_button]:bg-warning-strong [&_button:hover]:opacity-80 [&_a]:bg-warning-strong [&_a:hover]:opacity-80`,
        secondary: `[&_button]:bg-transparent [&_button]:border-warning-strong [&_button]:text-warning-primary [&_button:hover]:bg-warning-soft [&_a]:bg-transparent [&_a]:border-warning-strong [&_a]:text-warning-primary [&_a:hover]:bg-warning-soft`,
        text: `[&_button]:text-warning-primary [&_button:hover]:bg-warning-soft [&_a]:text-warning-primary [&_a:hover]:bg-warning-soft`,
        tonal: '',
    },
    error: {
        primary: `[&_button]:bg-negative-strong [&_button:hover]:opacity-80 [&_a]:bg-negative-strong [&_a:hover]:opacity-80`,
        secondary: `[&_button]:bg-transparent [&_button]:border-negative-strong [&_button]:text-negative-primary [&_button:hover]:bg-negative-soft [&_a]:bg-transparent [&_a]:border-negative-strong [&_a]:text-negative-primary [&_a:hover]:bg-negative-soft`,
        text: `[&_button]:text-negative-primary [&_button:hover]:bg-negative-soft [&_a]:text-negative-primary [&_a:hover]:bg-negative-soft`,
        tonal: '',
    },
};
const getVariantTintClasses = (bannerVariant, buttonVariant) => {
    if (bannerVariant === 'default' || bannerVariant === 'raised') {
        return '';
    }
    return VARIANT_TINT_CLASSES[bannerVariant][buttonVariant] ?? '';
};
export function BannerAction(props) {
    const { ref, variant, children, disabled = false, track, ...restProps } = props;
    const { size, variant: bannerVariant } = useBannerContext();
    const targetRef = useMeasureableHandle(ref);
    // Manually pick the props that Radix UI passes in for their own internal usage
    // and pass them down to the Button or Link.
    const __radixProps = useRadixProps(restProps);
    const actionSize = size === 'default' ? 'small' : 'tiny';
    // Determine if rendering as Link based on href presence
    const shouldRenderAsLink = Boolean('href' in props && props.href);
    // Common props shared by both Button and Link
    const commonProps = {
        ref: targetRef,
        ...__radixProps,
        size: actionSize,
        disabled,
        fullWidth: true,
        variant,
        children,
        track,
    };
    let action;
    if (shouldRenderAsLink) {
        const { href, target, rel, hardNav } = props;
        action = _jsx(Link, { ...commonProps, href: href, target: target, rel: rel, hardNav: hardNav });
    }
    else {
        const { onClick } = props;
        action = _jsx(Button, { ...commonProps, onClick: onClick });
    }
    const wrapperClasses = classNames('w-full @xl/banner:w-auto', getVariantTintClasses(bannerVariant, variant));
    return _jsx("div", { className: wrapperClasses, children: action });
}
//# sourceMappingURL=BannerAction.js.map